import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import OrganizationDivisionRepository from '@src/repositories/OrganizationDivisionRepository';

/**
 * The operation caps a request at 100 ids (MAX_DIVISION_SUMMARIES); one slot is reserved for the
 * anchor division added below, so chunks hold 99 unresolved ids at most.
 */
const SUMMARIES_CHUNK_SIZE = 99;

/** Shown when a division cannot be named: an id tells the user nothing. */
export const UNKNOWN_DIVISION_NAME = '-';

interface AssignedDivision {
  name?: string;
}

export interface UseDivisionNamesOptions {
  organizationId: string;
  /** Every division id rendered on the current page, in any order and with repeats. */
  divisionIds: string[];
  /** The user's own divisions (`state.divisions.assigned.data.divisions`), already carrying names. */
  assignedDivisions: Record<string, AssignedDivision>;
  enabled?: boolean;
}

export interface UseDivisionNamesResult {
  /** Name for a division id, or `'-'` while it is unresolved or unresolvable. */
  getDivisionName: (divisionId: string) => string;
}

const chunk = <T>(items: T[], size: number): T[][] =>
  items.reduce((chunks: T[][], item, index) => {
    if (index % size === 0) {
      chunks.push([]);
    }
    chunks[chunks.length - 1].push(item);
    return chunks;
  }, []);

/**
 * Names the divisions a form or trigger template is scoped to, including the ones outside the
 * user's own scope.
 *
 * The user's membership document only carries their own divisions, so a division-scoped user used
 * to render raw ids for everything else. Firestore cannot help here — `get` on a division document
 * is restricted to readers of that division, and widening it would let any organization member
 * read every division. Instead the missing ids go to `organizationDivision.summaries`, which
 * answers with names only and requires the caller to share at least one of the requested
 * divisions.
 *
 * Each id is requested at most once per organization; a failed lookup keeps the `'-'` fallback
 * rather than retrying, because a missing name never blocks the view.
 */
export const useDivisionNames = ({
  organizationId,
  divisionIds,
  assignedDivisions,
  enabled = true,
}: UseDivisionNamesOptions): UseDivisionNamesResult => {
  const [resolvedNames, setResolvedNames] = useState<Record<string, string>>({});
  const requestedIdsRef = useRef<Set<string>>(new Set());
  const repositoryRef = useRef<OrganizationDivisionRepository | null>(null);
  if (repositoryRef.current === null) {
    repositoryRef.current = new OrganizationDivisionRepository();
  }

  useEffect(() => {
    requestedIdsRef.current = new Set();
    setResolvedNames({});
  }, [organizationId]);

  // Sorted so the dependency key is stable regardless of row order.
  const wantedIdsKey = useMemo(
    () => Array.from(new Set(divisionIds.filter(Boolean))).sort().join(','),
    [divisionIds],
  );
  const assignedIdsKey = useMemo(() => Object.keys(assignedDivisions).sort().join(','), [
    assignedDivisions,
  ]);

  useEffect(() => {
    if (!enabled || !organizationId) return undefined;

    const wantedIds = wantedIdsKey.split(',').filter(Boolean);
    const missing = wantedIds.filter(
      divisionId =>
        !assignedDivisions[divisionId]?.name && !requestedIdsRef.current.has(divisionId),
    );

    if (!missing.length) return undefined;

    // The operation grants the request when the caller shares at least one of the *requested*
    // divisions, so asking for the unresolved ids alone is always denied: those are precisely the
    // ones outside the user's scope. Every request therefore carries an anchor -- a division the
    // user belongs to. Preferring one from the page keeps the ask truthful (these ids are named
    // because they sit on a row reaching into the user's own division); any other assigned
    // division is an acceptable fallback for the rare page whose rows are all out of scope.
    // Callers with an organization role need no anchor and having none is not an error for them.
    const anchorId =
      wantedIds.find(divisionId => assignedDivisions[divisionId]?.name) ??
      Object.keys(assignedDivisions).find(divisionId => assignedDivisions[divisionId]?.name);

    missing.forEach(divisionId => requestedIdsRef.current.add(divisionId));

    let cancelled = false;
    const repository = repositoryRef.current as OrganizationDivisionRepository;

    // Chunked with the anchor repeated in every chunk, so no single request loses its authorization.
    Promise.all(
      chunk(missing, SUMMARIES_CHUNK_SIZE).map(ids =>
        repository.summaries({
          organizationId,
          divisionIds: anchorId ? [anchorId, ...ids.filter(id => id !== anchorId)] : ids,
        }),
      ),
    )
      .then(batches => {
        if (cancelled) return;
        const names = batches
          .flat()
          .reduce(
            (result: Record<string, string>, division) => ({
              ...result,
              [division.id]: division.name,
            }),
            {},
          );
        if (Object.keys(names).length) {
          setResolvedNames(previous => ({ ...previous, ...names }));
        }
      })
      // Names are cosmetic: a failed lookup leaves the '-' fallback in place.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, organizationId, wantedIdsKey, assignedIdsKey]);

  const getDivisionName = useCallback(
    (divisionId: string) =>
      assignedDivisions[divisionId]?.name || resolvedNames[divisionId] || UNKNOWN_DIVISION_NAME,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignedIdsKey, assignedDivisions, resolvedNames],
  );

  return { getDivisionName };
};
